import { useEffect, useState } from "react";
import operatorApiService from "../../../services/operator-api.service";
import "../CommonDesign.scss";
import editIcon from "../../../assets/imgs/master/edit_icon_wh.svg";
import deleteIcon from "../../../assets/imgs/master/delete_icon_wh.svg";

interface RequestClassProps {
  baseId: number | null;
}

interface EditableRequestClass {
  confirmation_request_class_cd: string;
  base_id: number;
  confirmation_request_class_name: string;
  item_color: string;
  priority: number;
  status: "original" | "edited" | "new" | "deleted";
  isEditing?: boolean;
  internalId: string;
  original_cd: string;
}

const RequestClass = ({ baseId }: RequestClassProps) => {
  const [originalRows, setOriginalRows] = useState<EditableRequestClass[]>([]);
  const [rows, setRows] = useState<EditableRequestClass[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const colorLabelMap: Record<string, string> = {
    "alrt-gr": "1:緑",
    "alrt-gr2": "1:緑",
    "alrt-ye": "2:黄",
    "alrt-rd": "3:赤",
  };

  useEffect(() => {
    if (!baseId) return;

    const fetchRequestClasses = async () => {
      const res = await operatorApiService.doGetRequestClasses({ baseId });
      const initial = res
        .map((entry: any) => ({
          ...entry,
          original_cd: entry.confirmation_request_class_cd,
          status: "original",
          isEditing: false,
          internalId: `existing-${entry.confirmation_request_class_cd}`,
        }))
        .sort(
          (a: any, b: any) =>
            Number(a.confirmation_request_class_cd) -
            Number(b.confirmation_request_class_cd)
        );
      setOriginalRows(initial);
      setRows(initial);
    };

    fetchRequestClasses();
  }, [baseId]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        confirmation_request_class_cd: "",
        base_id: baseId!,
        confirmation_request_class_name: "",
        item_color: "alrt-gr2",
        priority: 0,
        status: "new",
        isEditing: true,
        internalId: `new-${Date.now()}-${Math.random()}`,
        original_cd: "",
      },
    ]);
  };

  const markDeleted = (internalId: string) => {
    setRows((prev) =>
      prev
        .filter((r) => {
          if (r.internalId !== internalId) return true;
          return r.status !== "new";
        })
        .map((r) =>
          r.internalId === internalId && r.status !== "new"
            ? { ...r, status: "deleted" }
            : r
        )
    );
  };

  const toggleEdit = (internalId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.internalId === internalId
          ? {
              ...r,
              isEditing: true,
              status: "edited",
              original_cd: r.original_cd ?? r.confirmation_request_class_cd,
            }
          : r
      )
    );
  };

  const handleChange = (
    index: number,
    field: keyof EditableRequestClass,
    value: any
  ) => {
    const updated = [...rows];
    (updated[index] as any)[field] = value;
    setRows(updated);
  };

  const submit = async () => {
    try {
      const hasBlank = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.confirmation_request_class_cd.trim() === "" ||
            r.confirmation_request_class_name.trim() === "" ||
            r.item_color.trim() === "" ||
            r.priority === null)
      );

      if (hasBlank) {
        setErrorMessage("空欄があります。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const hasTooLong = rows.some(
        (r) =>
          (r.status === "new" || r.status === "edited") &&
          (r.confirmation_request_class_cd.trim().length > 2 ||
            r.confirmation_request_class_name.trim().length > 30 ||
            r.item_color.trim().length > 30)
      );

      if (hasTooLong) {
        setErrorMessage("文字数が制限を超えています。");
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const create = rows
        .filter((r) => r.status === "new")
        .map(
          ({
            confirmation_request_class_cd,
            confirmation_request_class_name,
            item_color,
            priority,
          }) => ({
            confirmation_request_class_cd,
            confirmation_request_class_name,
            item_color,
            priority,
            base_id: baseId!,
          })
        );

      const update = rows
        .filter((r) => r.status === "edited")
        .map(
          ({
            confirmation_request_class_cd,
            confirmation_request_class_name,
            item_color,
            priority,
            original_cd,
          }) => ({
            confirmation_request_class_cd,
            confirmation_request_class_name,
            item_color,
            priority,
            original_cd,
          })
        );

      const del = rows
        .filter((r) => r.status === "deleted")
        .map((r) => r.confirmation_request_class_cd);

      if (create.length === 0 && update.length === 0 && del.length === 0) {
        return;
      }

      const response = await operatorApiService.saveRequestClasses({
        baseId,
        create,
        update,
        delete: del,
      });

      if (response.error === "duplicate") {
        setErrorMessage("指定された確認種別CDは既に使用されています。");
        setTimeout(() => setErrorMessage(null), 3000);
      }

      if (response.status === "success") {
        setSuccessMessage("更新に成功しました。");
        setTimeout(() => setSuccessMessage(null), 3000);

        const refreshed = await operatorApiService.doGetRequestClasses({
          baseId,
        });
        const mapped = refreshed
          .map((entry: any) => ({
            ...entry,
            status: "original",
            isEditing: false,
            internalId: `existing-${entry.confirmation_request_class_cd}`,
          }))
          .sort(
            (a: any, b: any) =>
              Number(a.confirmation_request_class_cd) -
              Number(b.confirmation_request_class_cd)
          );
        setRows(mapped);
        setOriginalRows(mapped);
      }
    } catch (error) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  const handleResetAll = () => {
    const reset = originalRows.map((r) => ({ ...r }));
    setRows(reset);
  };

  return (
    <div className="requestClass">
      <div className="content">
        <div className="title">確認要求種別</div>
        <div className="section">
          <div className="sectionTitle">確認要求種別一覧</div>
          <div className="sectionBody">
            <table className="tableArea">
              <thead>
                <tr>
                  <th>確認要求種別CD</th>
                  <th>確認要求種別名</th>
                  <th>画面項目色</th>
                  <th>優先順位</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.internalId}>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.confirmation_request_class_cd}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "confirmation_request_class_cd",
                              e.target.value
                            )
                          }
                        />
                      ) : (
                        row.confirmation_request_class_cd
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          value={row.confirmation_request_class_name}
                          onChange={(e) =>
                            handleChange(
                              i,
                              "confirmation_request_class_name",
                              e.target.value
                            )
                          }
                        />
                      ) : (
                        row.confirmation_request_class_name
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <select
                          value={row.item_color}
                          onChange={(e) =>
                            handleChange(i, "item_color", e.target.value)
                          }
                        >
                          <option value="alrt-gr2">1:緑</option>
                          <option value="alrt-ye">2:黄</option>
                          <option value="alrt-rd">3:赤</option>
                        </select>
                      ) : (
                        colorLabelMap[row.item_color] ?? row.item_color
                      )}
                    </td>
                    <td
                      className={row.status === "deleted" ? "deleteCell" : ""}
                    >
                      {row.isEditing ? (
                        <input
                          type="number"
                          value={row.priority}
                          onChange={(e) =>
                            handleChange(i, "priority", Number(e.target.value))
                          }
                        />
                      ) : (
                        row.priority
                      )}
                    </td>
                    <td>
                      {row.status === "deleted" ? (
                        <span className="deletionLabel">削除予定</span>
                      ) : row.status === "edited" ? (
                        <span className="editLabel">編集予定</span>
                      ) : row.status === "new" ? (
                        <button
                          className="cancelButton"
                          onClick={() => markDeleted(row.internalId)}
                        >
                          取消
                        </button>
                      ) : (
                        <div className="buttonGroup">
                          <button
                            className="updateButton"
                            onClick={() => toggleEdit(row.internalId)}
                          >
                            <img src={editIcon} alt="" className="icon" />
                            編集
                          </button>
                          <button
                            className="deleteButton"
                            onClick={() => markDeleted(row.internalId)}
                          >
                            <img src={deleteIcon} alt="" className="icon" />
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="createFooter">
              <div className="createLabel">
                表に新規入力欄が追加されます。
                新規確認要求種別を入力し、登録してください。
              </div>
              <button className="createButton" onClick={addRow}>
                新規行追加
              </button>
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
            {rows.some((r) => r.status !== "original") && (
              <div className="actionButtons">
                <button className="backButton" onClick={handleResetAll}>
                  戻る
                </button>
                <button className="decisionButton" onClick={submit}>
                  確定
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestClass;
